import {
  useAddCartItemMutation,
  useAddCartItemsMutation,
  useCartItemNoteMutation,
  useChangeCartItemQuantityMutation,
  useClearCurrentCartMutation,
  useRemoveCartItemMutation,
  useSaveCartAsDraftMutation,
} from '@api/cart.api';
import {
  CartAddRequest,
  CartChangeQuantityRequest,
  CartItemNoteRequest,
  CartRemoveRequest,
} from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useAppSelector } from '@hooks/redux/store.hooks';
import { useToast } from '@hooks/toast/toast.hooks';
import { getCartTotalItems } from '@store/cart/cart.slice';
import { t } from '@translations';
import { useCallback, useMemo } from 'react';

type WishlistItem = {
  code: string;
  quantity: number;
  productName: string;
};

type CartProps = {
  itemCountLabel: string | number;
  itemCount: number;
  addToCart: (code?: string, quantity?: number, productName?: string) => void;
  addToCartStatus: any;
  removeFromCart: (code?: string, productName?: string) => void;
  removeFromCartStatus: any;
  changeItemQuantity: ({
    code,
    quantity,
    productName,
  }: {
    code?: string;
    quantity?: number;
    productName?: string;
  }) => void;
  changeItemQuantityStatus: any;
  changeItemNote: (code?: string, note?: string, productName?: string) => void;
  changeItemNoteStatus: any;
  addItemsToCart: (wishlist: WishlistItem[]) => void;
  addItemsToCartStatus: any;
  clearCart: () => void;
  clearCartStatus: any;
};

type CartMessageHandlers = {
  getCartActionSuccessHandler: (messageCode: string, productName?: string) => () => void;
  getCartErrorHandler: (productName?: string) => () => void;
};

// Error / Success Handlers
//--------------------------
export const useCartMessageHandlers = (): CartMessageHandlers => {
  const { toast } = useToast();
  const getCartErrorHandler =
    (productName: string = 'Produkt') =>
    () =>
      toast({
        severity: 'error',
        message: t(TextCodes.Product.Cart.ProductCartError, { productName }),
      });

  const getCartActionSuccessHandler =
    (messageCode: string, productName: string = 'Produkt') =>
    () =>
      toast({
        severity: 'success',
        message: t(messageCode, { productName }),
      });
  return { getCartActionSuccessHandler, getCartErrorHandler };
};

export const useCart = (): CartProps => {
  const { getCartActionSuccessHandler, getCartErrorHandler } = useCartMessageHandlers();
  const [addToCartTrigger, addToCartStatus] = useAddCartItemMutation();
  const [removeFromCartTrigger, removeFromCartStatus] = useRemoveCartItemMutation();
  const [changeItemQuantityTrigger, changeItemQuantityStatus] = useChangeCartItemQuantityMutation();
  const [changeItemNoteTrigger, changeItemNoteStatus] = useCartItemNoteMutation();
  const [addItemsToCartTrigger, addItemsToCartStatus] = useAddCartItemsMutation();
  const [clearCartTrigger, clearCartStatus] = useClearCurrentCartMutation();
  // Add to cart
  //-------------
  const addToCart = useCallback(
    async (code?: string, quantity?: number, productName?: string) => {
      const request: CartAddRequest = {
        code,
        quantity,
      };
      await addToCartTrigger(request)
        .unwrap()
        .then(
          getCartActionSuccessHandler(TextCodes.Product.Cart.ProductAdded, productName),
          getCartErrorHandler(productName)
        );
    },
    [addToCartTrigger, getCartActionSuccessHandler, getCartErrorHandler]
  );

  const addItemsToCart = useCallback(
    async (wishlist: WishlistItem[]) => {
      const items: CartAddRequest[] = wishlist.map((item) => ({
        code: item.code,
        quantity: item.quantity,
      }));

      await addItemsToCartTrigger({ items })
        .unwrap()
        .then(
          () => {
            wishlist?.forEach((item) => {
              getCartActionSuccessHandler(TextCodes.Product.Cart.ProductAdded, item.productName)();
            });
          },
          () => {
            wishlist?.forEach((item) => {
              getCartErrorHandler(item.productName)();
            });
          }
        );
    },
    [addItemsToCartTrigger, getCartActionSuccessHandler, getCartErrorHandler]
  );
  // Remove from cart
  //------------------
  const removeFromCart = useCallback(
    (code?: string, productName?: string) => {
      const request: CartRemoveRequest = {
        code,
      };
      removeFromCartTrigger(request)
        .unwrap()
        .then(
          getCartActionSuccessHandler(TextCodes.Product.Cart.ProductRemoved, productName),
          getCartErrorHandler(productName)
        );
    },
    [removeFromCartTrigger, getCartActionSuccessHandler, getCartErrorHandler]
  );

  // Change item quantity
  //----------------------
  const changeItemQuantity = useCallback(
    ({
      code,
      quantity,
      productName,
    }: {
      code?: string;
      quantity?: number;
      productName?: string;
    }) => {
      const request: CartChangeQuantityRequest = {
        code,
        quantity,
      };
      changeItemQuantityTrigger(request)
        .unwrap()
        .then(
          getCartActionSuccessHandler(TextCodes.Product.Cart.ProductQuantityChanged, productName),
          getCartErrorHandler(productName)
        );
    },
    [changeItemQuantityTrigger, getCartActionSuccessHandler, getCartErrorHandler]
  );

  // Change item note
  //----------------------
  const changeItemNote = useCallback(
    (code?: string, note?: string, productName?: string) => {
      const request: CartItemNoteRequest = {
        code,
        note,
      };
      changeItemNoteTrigger(request)
        .unwrap()
        .then(
          getCartActionSuccessHandler(TextCodes.Product.Cart.ProductNoteChanged, productName),
          getCartErrorHandler(productName)
        );
    },
    [changeItemNoteTrigger, getCartActionSuccessHandler, getCartErrorHandler]
  );

  const clearCart = useCallback(() => {
    clearCartTrigger(null)
      .unwrap()
      .then(getCartActionSuccessHandler(TextCodes.Product.Cart.CartCleared), getCartErrorHandler());
  }, [clearCartTrigger, getCartActionSuccessHandler, getCartErrorHandler]);

  const itemCount = useAppSelector(getCartTotalItems);
  const itemCountLabel = useMemo(() => {
    return itemCount > 0
      ? t(TextCodes.Product.Cart.ProductCount, {
          productCount: itemCount.toString(),
          itemCount: itemCount.toString(),
        })
      : 0;
  }, [itemCount]);

  return useMemo(() => {
    return {
      itemCountLabel,
      itemCount,
      addToCart,
      addToCartStatus,
      removeFromCart,
      removeFromCartStatus,
      changeItemQuantity,
      changeItemQuantityStatus,
      changeItemNote,
      changeItemNoteStatus,
      addItemsToCart,
      addItemsToCartStatus,
      clearCart,
      clearCartStatus,
    };
  }, [
    itemCountLabel,
    itemCount,
    addToCart,
    addToCartStatus,
    removeFromCart,
    removeFromCartStatus,
    changeItemQuantity,
    changeItemQuantityStatus,
    changeItemNote,
    changeItemNoteStatus,
    addItemsToCart,
    addItemsToCartStatus,
    clearCart,
    clearCartStatus,
  ]);
};

export const useCartDraft = (): { saveCartAsDraft: () => void; saveCartAsDraftStatus: any } => {
  const { getCartActionSuccessHandler, getCartErrorHandler } = useCartMessageHandlers();
  const [saveCartAsDraftTrigger, saveCartAsDraftStatus] = useSaveCartAsDraftMutation();

  // Change item note
  //----------------------
  const saveCartAsDraft = () => {
    saveCartAsDraftTrigger(null)
      .unwrap()
      .then(
        getCartActionSuccessHandler(TextCodes.Product.Cart.CartSavedAsDraft),
        getCartErrorHandler()
      );
  };
  return {
    saveCartAsDraft,
    saveCartAsDraftStatus,
  };
};
